import { AxiosResponse } from "axios";
import { IAbstractModel } from "model/abstract.model";
import { IConsumidor } from "model/cliente.model";
import LoginService from "pages/login/login.service";
import { FlowCreatePedidoCieloTO, FlowPedidoCieloIsPagoTO } from "pages/pagamento/formapagamento/formapagamento.model";
import { TDadosCartao } from "pages/pagamento/pagamento.model";
import { useAppSelector } from "redux/hooks";
import { axiosRequest } from "../../axios/axios.config";
import { FlowVendedorDTO } from "./OutrosComissionados";
import { CupomTrocaSkuDTO, EstoqueTO, FlowConsultaCartaoPagoTO, FlowCreatePedidoCieloTOResponse, FlowVendaTO, IProduto } from "./registra-venda.model";

const RegistraVendaService = () => {
    const path = '/flow';
    const pessoa = '/pessoa';
    const loginService = LoginService();
    const pedido = useAppSelector(s => s.state.pedido);
    const depositoUUID = useAppSelector(s => s.configuracoes.configuracaoPedidoBalcao.deposito?.uuid);
    const { tableRows, tableRowsDevolucao, troco } = useAppSelector(s => s.pagamento);
    const { token: tokenAutorizador } = useAppSelector(e => e.configuracoes.configuracaoDescontoAutorizador);
    const tabelaPrecoSelecionada = useAppSelector(s => s.configuracoes.configuracaoPedidoBalcao.tabelaPreco);

    const getTabelaPrecoPedido = (): IAbstractModel | null => {
        return tabelaPrecoSelecionada?.uuid ? tabelaPrecoSelecionada : pedido.tabelaPreco;
    };

    const getPedido = (): FlowVendaTO => {
        return { ...pedido, tabelaPreco: getTabelaPrecoPedido(), itensPagamento: tableRows, itensPagamentoDevolucao: tableRowsDevolucao, troco };
    };

    return {
        novoPedido(): Promise<AxiosResponse<FlowVendaTO>> {
            return axiosRequest.get(`${path}/novo`, { headers: loginService.getHeaders() });
        },
        getConsumidoresAc(toSearch: string): Promise<AxiosResponse<Array<IConsumidor>>> {
            return axiosRequest.get(`${path}/consumidores`, { headers: loginService.getHeaders(), params: { toSearch } });
        },
        getVendedoresAc(
            toSearch: string,
            incluirGuias = false
        ): Promise<AxiosResponse<Array<FlowVendedorDTO>>> {
            return axiosRequest.get(`${path}/vendedores`, {
                headers: loginService.getHeaders(),
                params: { toSearch, incluirGuias },
            });
        },
        getGuiasAc(
            toSearch: string,
        ): Promise<AxiosResponse<Array<FlowVendedorDTO>>> {
            return axiosRequest.get(`${path}/guias`, {
                headers: loginService.getHeaders(),
                params: { toSearch },
            });
        },
        getVendedoresSemOutrosComissionadosAc(
            toSearch: string,
            uuidRepresentante?: string,
            uuidCliente?: string,
        ): Promise<AxiosResponse<Array<FlowVendedorDTO>>> {
            const params: Record<string, unknown> = { toSearch };
            if (uuidRepresentante) params.uuidRepresentante = uuidRepresentante;
            if (uuidCliente) params.uuidCliente = uuidCliente;

            return axiosRequest.get(`${path}/vendedores-sem-outros-comissionados`, {
                headers: loginService.getHeaders(),
                params,
            });
        },
        getVendedoresDoPedido(uuidPedido: string): Promise<AxiosResponse<{ vendedorPrincipal: string | null, outrosComissionados: string[] }>> {
            return axiosRequest.get(`${path}/vendedores-do-pedido/${uuidPedido}`, {
                headers: loginService.getHeaders(),
            });
        },
        getSkusAc(toSearch: string, tabelaPrecoUUID: string | null): Promise<AxiosResponse<Array<IProduto>>> {
            return axiosRequest.get(`${path}/descricaosku`, {
                headers: loginService.getHeaders(),
                params: {
                    toSearch,
                    tabelaPrecoUUID: tabelaPrecoUUID !== '' ? tabelaPrecoUUID : null
                }
            });
        },
        getProdutoByCodigo(codigoBarras: string, tabelaPrecoUUID: string | null): Promise<AxiosResponse<IProduto>> {
            return axiosRequest.get(`${path}/leitorproduto/`, {
                headers: loginService.getHeaders(),
                params: {
                    codigo: codigoBarras ?? null,
                    tabelaPrecoUUID: tabelaPrecoUUID !== '' ? tabelaPrecoUUID : null
                }
            });
        },
        getSkuPorCupomTroca(codigo: string): Promise<AxiosResponse<CupomTrocaSkuDTO>> {
            return axiosRequest.get(`/skutrocaserial/codigo/${encodeURIComponent(codigo)}`, {
                headers: loginService.getHeaders(),
            });
        },
        salvarVenda(isPrevenda = false): Promise<AxiosResponse<FlowVendaTO>> {
            return axiosRequest.put(`${path}/salvarvenda`, getPedido(), { headers: tokenAutorizador ? loginService.getHeadersWithAutorizadorToken() : loginService.getHeaders(), params: { depositoUUID, isPrevenda } });
        },
        finalizarVenda(isForcedNfe: boolean): Promise<AxiosResponse<{ pedido: FlowVendaTO, urlDanfe: string; }>> {
            return axiosRequest.post(`${path}/finalizarvenda`, getPedido(), {
                headers: tokenAutorizador ? loginService.getHeadersWithAutorizadorToken() : loginService.getHeaders(),
                params: { optionalModelo: null, outros: false, depositoUUID, isForcedNfe }
            });
        },
        finalizarVendaNFE(isForcedNfe: boolean): Promise<AxiosResponse<{ pedido: FlowVendaTO, urlDanfe: string; }>> {
            return axiosRequest.post(`${path}/finalizarvenda`, getPedido(), {
                headers: tokenAutorizador ? loginService.getHeadersWithAutorizadorToken() : loginService.getHeaders(),
                params: { optionalModelo: 'NFE', outros: false, depositoUUID, isForcedNfe }
            });
        },
        finalizarVendaOutros(isForcedNfe: boolean): Promise<AxiosResponse<{ pedido: FlowVendaTO; }>> {
            return axiosRequest.post(`${path}/finalizarvenda`, getPedido(), {
                headers: tokenAutorizador ? loginService.getHeadersWithAutorizadorToken() : loginService.getHeaders(),
                params: { optionalModelo: null, outros: true, depositoUUID, isForcedNfe }
            });
        },
        getEstoque(uuids: string[]): Promise<AxiosResponse<Array<EstoqueTO>>> {
            return axiosRequest.post(`${path}/estoque`, { uuids }, { headers: loginService.getHeaders(), params: { depositoUUID } });
        },
        getTransportadoras(): Promise<AxiosResponse<Array<IAbstractModel>>> {
            return axiosRequest.get(`${pessoa}/transportadoras`, { headers: loginService.getHeaders() });
        },
        getDadosPagamentoCartao(): Promise<AxiosResponse<TDadosCartao>> {
            return axiosRequest.get(`${pessoa}/dadospagamentocartao`, { headers: loginService.getHeaders() });
        },
        createFlowPagamentoCartao(flowCreatePedidoCieloTO: FlowCreatePedidoCieloTO): Promise<AxiosResponse<FlowCreatePedidoCieloTOResponse>> {
            return axiosRequest.post(`pedidopagamentocartao/createflowpagamentocartao`, flowCreatePedidoCieloTO, { headers: loginService.getHeaders() });
        },
        cartaoIntegracaoArePago(itens: Array<FlowPedidoCieloIsPagoTO>): Promise<AxiosResponse<FlowConsultaCartaoPagoTO>> {
            return axiosRequest.post(`pedidopagamentocartao/cartaointegracaoarepago`, itens, { headers: loginService.getHeaders() });
        },
        getCanaisVenda(): Promise<AxiosResponse<Array<IAbstractModel>>> {
            return axiosRequest.get(`canaldevenda/listar-canais`, { headers: loginService.getHeaders() });
        },
        setCanalVendaPadrao(uuidCanalVenda: string): Promise<AxiosResponse<IAbstractModel | null>> {
            return axiosRequest.put(`canaldevendausuariopadrao/${uuidCanalVenda}`, null, { headers: loginService.getHeaders() });
        },
        getCanalVendaPadrao(): Promise<AxiosResponse<IAbstractModel | null>> {
            return axiosRequest.get(`canaldevendausuariopadrao`, { headers: loginService.getHeaders() });
        }
    };
};

export default RegistraVendaService;
